import { CellRenderer, CellValue, ColumnAnalysis, ParsedFile, PreviewLayout, RendererKind } from '../types';

import { audioRecordingRenderer } from './audio';
import { audioFileRenderer } from './audioFile';
import { conversationRenderer } from './conversation';
import { longtextRenderer } from './longtext';
import { markdownRenderer } from './markdown';
import { plainRenderer } from './plain';
import { structuredRenderer } from './structured';

const SAMPLE_SIZE = 20;
const RECORDS_MEDIAN_THRESHOLD = 200;

// Ordered specific → general. plain must stay last (universal fallback).
// audioRecordingRenderer first — it requires both a matching column name and integer values,
// so it can never misfire. conversation before structured (a transcript is also an object
// array); markdown before longtext (a report is also a long string).
const renderers: CellRenderer[] = [
  audioRecordingRenderer,
  audioFileRenderer,
  conversationRenderer,
  markdownRenderer,
  structuredRenderer,
  longtextRenderer,
  plainRenderer,
];

export function getRenderer(kind: RendererKind): CellRenderer {
  const renderer = renderers.find(r => r.kind === kind);
  if (!renderer) throw new Error(`Unknown renderer kind: ${kind}`);
  return renderer;
}

function isEmpty(value: CellValue): boolean {
  return value === null || value === undefined || value === '';
}

function cellLength(value: CellValue): number {
  if (typeof value === 'string') return value.length;
  if (typeof value === 'object' && value !== null) return JSON.stringify(value).length;
  return String(value).length;
}

function median(nums: number[]): number {
  if (nums.length === 0) return 0;
  const sorted = [...nums].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}

export function analyzeColumns(parsed: ParsedFile): ColumnAnalysis[] {
  return parsed.headers.map(id => {
    const samples: CellValue[] = [];
    parsed.rows.some(row => {
      const value = row[id];
      if (!isEmpty(value)) samples.push(value);
      return samples.length >= SAMPLE_SIZE;
    });

    const renderer = renderers.find(r => r.detect(samples, id)) ?? plainRenderer;
    const medianLength = median(samples.map(cellLength));

    return { id, kind: renderer.kind, medianLength };
  });
}

export function recommendLayout(analyses: ColumnAnalysis[], opts?: { preferRecords?: boolean }): PreviewLayout {
  if (opts?.preferRecords) return 'records';
  if (analyses.some(a => a.medianLength > RECORDS_MEDIAN_THRESHOLD)) return 'records';
  return 'grid';
}
